import { handleResponseOld, makeGet, makePost } from './helpers';

/**
 * @category Item API
 * @see fetchSavedSearches
 */
type FetchSavedSearchesParams = {
    authToken: string;
    deployment: string;
};

type FetchSavedSearchesResponse = {
    data: [
        {
            facets: any[];
            lastProcessed: string;
            lastSent: string;
            lastStatus: string;
            savedSearchId: number;
            sendAlert: boolean;
            userId: number;
        }
    ];
    message: string;
    success: boolean;
};
/**
 * fetches Saved Search facets via Get request to Item API
 * @function fetchSavedSearches
 * @category Item API
 * @param {FetchSavedSearchParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/savedsearch/facets
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/saved-search/routes/get-saved-searches-facets.ts
 */
const fetchSavedSearches = ({ authToken, deployment }: FetchSavedSearchesParams) =>
    new Promise<FetchSavedSearchesResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>savedsearch/facets' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see fetchSavedSearches
 */
type FetchSavedSearchItemsParams = {
    authToken: string;
    deployment: string;
    filter: string;
    page: number;
    pageSize: number;
    sort: string;
};

type FetchSavedSearchItemsResponse = {
    error: boolean;
    payload: {
        data: [
            {
                itemId: number;
                publishDate: string;
                saleStartTime: string;
            }
        ];
        page: number;
        pageSize: number;
        totalRecords: number;
    };
};
/**
 * returns all Saved search items via Get request to Saved Search API
 * @function fetchSavedSearchItems
 * @category Saved Search API
 * @param {FetchSavedSearchItemsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/savedsearch/getsavedsearchitems
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/511rzc/methods/GET
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/GetSavedSearchItems
 */
const fetchSavedSearchItems = ({ authToken, deployment, filter, page, pageSize, sort }: FetchSavedSearchItemsParams) =>
    new Promise<FetchSavedSearchItemsResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<SAVED-SEARCH-API>getsavedsearchitems' });
        request.query({ filter, page, pageSize, sort });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see deleteSavedSearchUrl
 */
type DeleteSavedSearchUrlParams = {
    deployment: string;
    savedSearchId: any;
    signature: string;
};
/**
 * removes a saved search via Post to Saved Search API
 * @function deleteSavedSearchUrl
 * @category Saved Search API
 * @param {DeleteSavedSearchUrlParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/savedsearch/deletesavedsearchurl
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/h6r6de/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/DeleteSavedSearchFromURL
 */
const deleteSavedSearchUrl = ({ deployment, savedSearchId, signature }: DeleteSavedSearchUrlParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'deletesavedsearchurl',
            deployment,
            path: '<SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ action: 'deleteSavedSearch', savedSearchId: Number(savedSearchId), signature });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postDeleteSavedSearch
 */
type PostDeleteSavedSearchParams = {
    authToken: string;
    deployment: string;
    savedSearchId: any;
};
/**
 * Deletes a saved search via Post to Saved Search API
 * @function postDeleteSavedSearch
 * @category Saved Search API
 * @param {PostDeleteSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/deletesavedsearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/y3zwdy/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/DeleteSavedSearch
 */
const postDeleteSavedSearch = ({ authToken, deployment, savedSearchId }: PostDeleteSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'deletesavedsearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ savedSearchId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postIsSavedSearch
 */
type PostIsSavedSearchParams = {
    authToken: string;
    deployment: string;
    ssParams: any;
};
/**
 * checks a saved search resource exists via Post to Saved Search API
 * @function postIsSavedSearch
 * @category Saved Search API
 * @param {PostIsSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/getsearchsaved/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/iaoggw/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/GetSearchSaved
 */
const postIsSavedSearch = ({ authToken, deployment, ssParams }: PostIsSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'getsearchsaved',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ facets: ssParams });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postSaveSearch
 */
type PostSaveSearchParams = {
    authToken: string;
    deployment: string;
    sendAlert: boolean;
    ssParams: any;
};
/**
 * Saves a search via Post to to Saved Search API
 * @function postSaveSearch
 * @category Saved Search API
 * @param {PostSaveSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/savesearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/hikxao/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/SaveSearch
 */
const postSaveSearch = ({ authToken, deployment, sendAlert, ssParams }: PostSaveSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'savesearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ facets: ssParams, sendAlert });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postSubscribeAllToSavedSearch
 */
type PostSubscribeAllToSavedSearchParams = {
    authToken: string;
    deployment: string;
};
/**
 * Subscribes to all saved search alerts via Post to Saved Search API
 * @function postSubscribeAllFromSavedSearch
 * @category Saved Search API
 * @param {PostSubscribeAllToSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/subscribesavedsearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/szkri0/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/SubscribeSavedSearch
 */
const postSubscribeAllFromSavedSearch = ({ authToken, deployment }: PostSubscribeAllToSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'subscribesavedsearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ sendAlert: true, updateAll: true });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postSubscribeToSavedSearch
 */
type PostSubscribeToSavedSearchParams = {
    authToken: string;
    deployment: string;
    savedSearchId: any;
};
/**
 * Subscribes to a saved search alert via Post to Saved Search API
 * @function postSubscribeToSavedSearch
 * @category Saved Search API
 * @param {PostSubscribeToSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/subscribesavedsearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/szkri0/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/SubscribeSavedSearch
 */
const postSubscribeToSavedSearch = ({ authToken, deployment, savedSearchId }: PostSubscribeToSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'subscribesavedsearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ savedSearchId, sendAlert: true });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postUnsubscribeAllFromSavedSearch
 */
type PostUnsubscribeAllFromSavedSearchParams = {
    authToken: string;
    deployment: string;
};
/**
 * Unsubscribes to all saved search alerts via Post to Saved Search API
 * @function postUnsubscribeAllFromSavedSearch
 * @category Saved Search API
 * @param {PostUnsubscribeAllFromSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/subscribesavedsearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/szkri0/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/SubscribeSavedSearch
 */
const postUnsubscribeAllFromSavedSearch = ({ authToken, deployment }: PostUnsubscribeAllFromSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'subscribesavedsearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ sendAlert: false, updateAll: true });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Saved Search API
 * @see postUnsubscribeFromSavedSearch
 */
type PostUnsubscribeFromSavedSearchParams = {
    authToken: string;
    deployment: string;
    savedSearchId: any;
};
/**
 * Unsubscribes to a saved search alert via Post to Saved Search API
 * @function postUnsubscribeFromSavedSearch
 * @category Saved Search API
 * @param {PostUnsubscribeFromSavedSearchParams}
 * @see https://api.liveauctioneers.com/savedsearch/subscribesavedsearch/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/9fwh09jse1/resources/szkri0/methods/POST
 * @see https://github.com/LIVEauctioneers/savedsearch-api/tree/master/functions/SubscribeSavedSearch
 */
const postUnsubscribeFromSavedSearch = ({
    authToken,
    deployment,
    savedSearchId,
}: PostUnsubscribeFromSavedSearchParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'subscribesavedsearch',
            authToken,
            deployment,
            path: '<LEGACY-SAVED-SEARCH-API>',
            useCacheKey: false,
        });
        request.send({ savedSearchId, sendAlert: false });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    deleteSavedSearchUrl,
    fetchSavedSearches,
    fetchSavedSearchItems,
    postDeleteSavedSearch,
    postIsSavedSearch,
    postSaveSearch,
    postSubscribeAllFromSavedSearch,
    postSubscribeToSavedSearch,
    postUnsubscribeAllFromSavedSearch,
    postUnsubscribeFromSavedSearch,
};
